import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  HStack,
  InputRightElement,
  Stack,
  Box,
  useToast,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useUpdateUserMutation } from '../../../../redux/api/userApi';
import { errorToast, okToast } from '../../../../utils/toast';
import { useTranslation } from 'react-i18next';

export const FormModalEditProfile = ({ isOpen, onClose, userData }: any) => {
  const [showPassword, setShowPassword] = useState(false);
  const { t } = useTranslation();
  const toast = useToast();
  const [clickOnSubmit, setClickOnSubmit] = useState(false);
  const [updateUser] = useUpdateUserMutation();

  // Estado inicial seguro (evita undefined)
  const [inputCreateUser, setInputCreateUser] = useState({
    name: userData?.name ?? '',
    lastname: userData?.lastname ?? '',
    password: '',
    confPassword: '',
  });

  // Sincroniza cuando cambie userData (por ejemplo, tras cargar /me)
  useEffect(() => {
    setInputCreateUser((prev) => ({
      ...prev,
      name: userData?.name ?? '',
      lastname: userData?.lastname ?? '',
    }));
  }, [userData?.name, userData?.lastname]);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setInputCreateUser((prev) => ({ ...prev, [name]: value }));
  };

  // Validación tolerante: nunca llama a .trim() sobre undefined
  const checkValue = (value: any, isOptional = true) => {
    const v = (value ?? '').toString();
    if (isOptional) return clickOnSubmit && v.length === 0;
    return clickOnSubmit && v.trim() === '';
  };

  const onSubmit = async () => {
    setClickOnSubmit(true);
    try {
      const name = (inputCreateUser?.name ?? '').trim();
      const lastname = (inputCreateUser?.lastname ?? '').trim();
      const password = (inputCreateUser?.password ?? '').toString();
      const confPassword = (inputCreateUser?.confPassword ?? '').toString();

      if (name === '' || lastname === '' || password !== confPassword) {
        return errorToast(
          toast,
          t('Empty fields passwords match'),
          t('Review required fields'),
        );
      }

      // Construye payload saneado
      const payload: any = {
        ...inputCreateUser,
        userId: userData?.userId || userData?.id,
        name,
        lastname,
      };
      // Si no quieres enviar password vacío, elimínalo del payload
      if (!password) delete payload.password;
      if (!confPassword) delete payload.confPassword;

      await updateUser(payload).unwrap();

      okToast(toast, t('User updated'));
      onClose?.();
    } catch (error: any) {
      console.error(error);
      const errMsg = error?.data?.message || error?.message;
      if (errMsg === 'You are not authorized to update this user') {
        errorToast(toast, 'No está autorizado para editar el usuario');
      } else {
        errorToast(toast, t('Unexpected error'), errMsg);
      }
    } finally {
      setClickOnSubmit(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('Edit profile')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Stack spacing={4}>
            <HStack>
              <Box>
                <FormControl
                  id="name"
                  isRequired
                  isInvalid={checkValue(inputCreateUser?.name, false)}
                >
                  <FormLabel>{t('Name')}:</FormLabel>
                  <Input
                    name="name"
                    type="text"
                    errorBorderColor="crimson"
                    value={inputCreateUser?.name ?? ''}
                    onChange={handleChange}
                  />
                </FormControl>
              </Box>
              <Box>
                <FormControl
                  id="lastname"
                  isRequired
                  isInvalid={checkValue(inputCreateUser?.lastname, false)}
                >
                  <FormLabel>{t('Surnames')}:</FormLabel>
                  <Input
                    name="lastname"
                    type="text"
                    errorBorderColor="crimson"
                    value={inputCreateUser?.lastname ?? ''}
                    onChange={handleChange}
                  />
                </FormControl>
              </Box>
            </HStack>

            <FormControl
              id="password"
              isInvalid={checkValue(inputCreateUser?.password, false)}
            >
              <FormLabel>{t('Password')}:</FormLabel>
              <InputGroup>
                <Input
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={inputCreateUser?.password ?? ''}
                  onChange={handleChange}
                />
                <InputRightElement h={'full'}>
                  <Button
                    variant={'ghost'}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <ViewIcon /> : <ViewOffIcon />}
                  </Button>
                </InputRightElement>
              </InputGroup>
            </FormControl>

            <FormControl id="confPassword">
              <FormLabel>{t('Repeat password')}:</FormLabel>
              <InputGroup>
                <Input
                  name="confPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={inputCreateUser?.confPassword ?? ''}
                  onChange={handleChange}
                />
                <InputRightElement h={'full'}>
                  <Button
                    variant={'ghost'}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <ViewIcon /> : <ViewOffIcon />}
                  </Button>
                </InputRightElement>
              </InputGroup>
            </FormControl>
          </Stack>
        </ModalBody>

        <ModalFooter>
          <Button
            size="lg"
            w={'100%'}
            bg={'teal300'}
            transition={'0.5s'}
            _hover={{
              bg: 'gray.600',
              color: 'white',
            }}
            color={'white'}
            colorScheme="teal"
            onClick={onSubmit}
            isLoading={clickOnSubmit}
          >
            {t('Update')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
